import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useFrontendAuth } from '../hooks/useAuth';
import { getMyProducts } from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { translateWithFallback } from '../utils/translateWithFallback';

const DISMISS_KEY = 'seller_onboarding_dismissed_until';
const DISMISS_DAYS = 7;

function isDismissed(): boolean {
    const until = localStorage.getItem(DISMISS_KEY);
    if (!until) return false;
    return Date.now() < Number(until);
}

function dismissForDays(days: number) {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
}

export default function SellerOnboardingBanner() {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const { isAuthenticated, isAuthReady } = useFrontendAuth();
    const [dismissed, setDismissed] = useState(isDismissed);

    const { data: myProducts, isLoading: productsLoading } = useProtectedQuery({
        queryKey: ['myProducts', 'onboarding-banner'],
        queryFn: getMyProducts,
        enabled: isAuthReady && isAuthenticated,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        setDismissed(isDismissed());
    }, []);

    const handleDismiss = () => {
        dismissForDays(DISMISS_DAYS);
        setDismissed(true);
    };

    if (dismissed) return null;

    // Phase 10b: zero-listing authenticated only (no guest banner — avoids double Sell CTA with hero/header)
    const showForZeroProducts =
        isAuthenticated &&
        isAuthReady &&
        !productsLoading &&
        Array.isArray(myProducts) &&
        myProducts.length === 0;

    if (!showForZeroProducts) return null;

    return (
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-[color-mix(in_srgb,var(--accent-color),transparent_50%)] shadow-sm">
            <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
                <div className="max-w-lg rounded-2xl bg-[color-mix(in_srgb,var(--bg-color),white_15%)] p-6 shadow-md">
                    <h2 className="text-xl font-bold text-[var(--text-color)] sm:text-2xl">
                        {tr('seller.banner.title', 'Ready to pass on your baby gear?')}
                    </h2>
                    <Link
                        to="/sell"
                        className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-[var(--accent-color)] px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90 sm:w-auto"
                    >
                        {tr('seller.banner.cta', 'Start selling')}
                    </Link>
                    <Link
                        to="/how-it-works"
                        className="mt-3 block text-center text-sm font-medium text-[var(--text-color)] underline hover:opacity-80 sm:text-left"
                    >
                        {tr('seller.banner.howitworks', 'How it works')}
                    </Link>
                </div>
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="absolute right-3 top-3 rounded-full p-1.5 text-[color-mix(in_srgb,var(--text-color),transparent_40%)] hover:bg-[color-mix(in_srgb,var(--bg-color),white_30%)]"
                    aria-label={tr('common.close', 'Close')}
                >
                    <X className="h-5 w-5" />
                </button>
            </div>
        </div>
    );
}